import { Link } from "react-router-dom";
import { ArrowLeft, History, Lock, Minus, Package, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { credits, rupees } from "../products/types";
import { CheckoutSummary } from "./checkout-summary";
import { PlanAmountEditor } from "./plan-amount-editor";
import { useCart, useCartMutations } from "./queries";
import type { PricedCartItem } from "./types";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

const units = (count: number) => `${count} item${count === 1 ? "" : "s"}`;

/**
 * /cart: the account's cart. While it is locked at checkout the page shows the
 * checkout summary; that comes from the server, so it survives a refresh or
 * leaving the page, and "Back to cart" unlocks it with its items.
 */
export function CartPage() {
  const cart = useCart();
  const m = useCartMutations();
  const toast = useToast();

  const fail = (title: string) => (err: unknown) => toast.danger(title, errorMessage(err, "Try again."));
  const busy = m.setQuantity.isPending || m.setAmount.isPending || m.remove.isPending || m.checkout.isPending;

  const setQuantity = (item: PricedCartItem, quantity: number) =>
    m.setQuantity.mutate({ productId: item.product_id, quantity }, { onError: fail("Could not update quantity") });

  const setAmount = (item: PricedCartItem, amount: number) =>
    m.setAmount.mutate({ productId: item.product_id, amount }, { onError: fail("Could not update the amount") });

  const remove = (item: PricedCartItem) =>
    m.remove.mutate(item.product_id, {
      onSuccess: () => toast.info("Removed from cart", item.name),
      onError: fail("Could not remove item"),
    });

  const checkout = () => m.checkout.mutate(undefined, { onError: fail("Could not check out") });
  const backToCart = () => m.reopen.mutate(undefined, { onError: fail("Could not open your cart") });

  if (cart.data?.status === "cart_checkout") {
    return <CheckoutSummary cart={cart.data} onBackToCart={backToCart} reopening={m.reopen.isPending} />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-8">
      <header>
        <div className="flex items-center justify-between gap-4">
          <Link to="/products" className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-2 hover:text-brand">
            <ArrowLeft className="h-4 w-4" /> Back to products
          </Link>
          <Link to="/orders" className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-2 hover:text-brand">
            <History className="h-4 w-4" /> Order history
          </Link>
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-ink">Your cart</h1>
        {cart.data && cart.data.item_count > 0 && (
          <p className="mt-1.5 text-sm text-ink-2">{units(cart.data.item_count)}</p>
        )}
      </header>

      {cart.isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-soft" />)}
        </div>
      ) : cart.isError ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-elevated p-12 text-center">
          <p className="text-sm text-danger">{errorMessage(cart.error, "Could not load your cart.")}</p>
          <button onClick={() => { void cart.refetch(); }} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Retry
          </button>
        </div>
      ) : !cart.data || cart.data.items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line bg-elevated px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand">
            <ShoppingCart className="h-6 w-6" />
          </span>
          <p className="text-base font-semibold text-ink">Your cart is empty</p>
          <p className="max-w-sm text-sm text-ink-2">Add plans and credit packs from the Products page.</p>
          <Link to="/products" className="mt-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
            Browse products
          </Link>
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[1fr_340px] lg:items-start">
          <ul className="space-y-3">
            {cart.data.items.map((item) => (
              <li
                key={item.product_id}
                aria-label={`Cart item ${item.name}`}
                className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-elevated p-5 shadow-sm"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
                  <Package className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-ink">{item.name}</p>
                  {item.rate_per_minute !== null ? (
                    <p className="text-sm tabular-nums text-ink-3">
                      {rupees(item.rate_per_minute)} / min ·{" "}
                      <span className="font-semibold text-ink-2">{credits(item.credits)} credits</span>
                    </p>
                  ) : (
                    <p className="text-sm tabular-nums text-ink-3">{rupees(item.unit_price)} each</p>
                  )}
                </div>
                {item.rate_per_minute !== null ? (
                  <PlanAmountEditor item={item} disabled={busy} onChange={(amount) => setAmount(item, amount)} />
                ) : (
                  <div className="flex items-center rounded-lg border border-line" role="group" aria-label={`Quantity of ${item.name}`}>
                    <button
                      onClick={() => setQuantity(item, item.quantity - 1)}
                      disabled={busy || item.quantity <= 1}
                      aria-label="Decrease quantity"
                      className="flex h-9 w-9 items-center justify-center text-ink-2 hover:bg-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-10 text-center text-sm font-semibold tabular-nums text-ink" aria-live="polite">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(item, item.quantity + 1)}
                      disabled={busy}
                      aria-label="Increase quantity"
                      className="flex h-9 w-9 items-center justify-center text-ink-2 hover:bg-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <span className="w-28 text-right text-base font-bold tabular-nums text-ink">{rupees(item.total_price)}</span>
                <button
                  onClick={() => remove(item)}
                  disabled={busy}
                  aria-label={`Remove ${item.name}`}
                  className="rounded-lg p-2 text-ink-3 hover:bg-danger-soft hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <aside className="space-y-4 rounded-2xl border border-line bg-elevated p-6 shadow-sm lg:sticky lg:top-6" aria-label="Order summary">
            <h2 className="text-lg font-semibold text-ink">Order summary</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between text-ink-2">
                <dt>Items</dt>
                <dd className="tabular-nums">{cart.data.item_count}</dd>
              </div>
              {cart.data.credits > 0 && (
                <div className="flex justify-between text-ink-2">
                  <dt>Credits (minutes)</dt>
                  <dd className="font-semibold tabular-nums text-ink">{credits(cart.data.credits)}</dd>
                </div>
              )}
              <div className="flex justify-between border-t border-line pt-3 text-base font-bold text-ink">
                <dt>Subtotal</dt>
                <dd className="tabular-nums">{rupees(cart.data.total)}</dd>
              </div>
            </dl>
            <p className="text-xs text-ink-3">All prices are exclusive of GST.</p>
            <button
              onClick={checkout}
              disabled={busy}
              className={cn(
                "flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold text-white",
                busy ? "cursor-wait bg-line text-ink-3" : "bg-brand hover:bg-brand-hover",
              )}
            >
              <Lock className="h-4 w-4" /> {m.checkout.isPending ? "Checking out…" : "Checkout"}
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
